// OWNER: FRONTEND-ADMIN. Every API call the admin screens make, in one place, typed against the shared contracts
// (src/shared/api.ts, api-core.ts from CORE, api-ai.ts from AI-TRAINING). A server-side shape change is a one-line fix here.
import { api } from '../api';
import type {
  Campaign, CampaignTemplate, Country, FunnelRow, HourBucket, ImportReport, OurNumber, StateRule, VmRecording,
} from '../../../src/shared/api';
import type {
  AuditEntry, CampaignCloneRequest, CampaignCreateRequest, CampaignPatchRequest, CampaignUser, DncEntry, ImportSummary,
  LookupPending, LookupRunResult, MemberRow, Page, SettingsPatch, SettingsView, StateRulePatch, TodayReport, UserAdmin,
  UserCreateRequest, UserPatchRequest,
} from '../../../src/shared/api-core';
import type { AiStatus, BriefsRunResponse } from '../../../src/shared/api-ai';
import type { DiscordTestResult } from '../../../src/shared/api-booked-calls';
import type {
  AvailableNumber, ManualNumberEventKind, NumberBuyRequest, NumberDailyRow, NumberEvent, NumberEventKind, NumberImportRequest,
  NumberPatchRequest, RecordingReviewRequest,
} from '../../../src/shared/api-telephony';

/** Accept a bare array or a { items: [...] } page, and fail loudly on anything else. */
function list<T>(what: string) {
  return (data: unknown): T[] => {
    if (Array.isArray(data)) return data as T[];
    if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) return (data as { items: T[] }).items;
    throw new Error(`The server sent an unexpected response for ${what}. Tell PJ.`);
  };
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export type { AvailableNumber, NumberEvent, NumberEventKind, ManualNumberEventKind, NumberDailyRow };
export type RecordingReview = RecordingReviewRequest & { reviewNote: string };

/** The create form also sends the Calendly event. If the route ignores it, the page PATCHes it right after
 *  (CampaignPatchRequest has calendlyEventSlug), so this works with or without CORE accepting it on create. */
export type CampaignCreate = CampaignCreateRequest & { calendlyEventSlug?: string };

/** `mailing_address` setting (CORE, fix round 1: CASL/CAN-SPAM postal address for every email). Typed here until
 *  api-core's SettingsView has it. `undefined` = this server doesn't have the setting yet; `null` = not set. */
export type AdminSettings = SettingsView & { mailingAddress?: string | null };
export type AdminSettingsPatch = SettingsPatch & { mailingAddress?: string | null };

/** State-rule conditions (migration 0005: scrub_all_lines, business_lines_only, setter_note). Typed here until
 *  api-core's StateRulePatch has them; the page checks the returned rule and says so if a field wasn't saved. */
export type AdminStateRulePatch = StateRulePatch & { scrubAllLines?: boolean; businessLinesOnly?: boolean; setterNote?: string | null; handPickOnly?: boolean; noSunday?: boolean };

export const adminApi = {
  // campaigns (CORE)
  campaigns: (status?: string) => api.get<unknown>(`/api/campaigns${qs({ status })}`).then(list<Campaign>('campaigns')),
  campaign: (id: number) => api.get<Campaign>(`/api/campaigns/${id}`),
  templates: () => api.get<unknown>('/api/campaigns/templates').then(list<CampaignTemplate>('campaign templates')),
  createCampaign: (req: CampaignCreate) => api.post<Campaign>('/api/campaigns', req),
  updateCampaign: (id: number, patch: CampaignPatchRequest) => api.patch<Campaign>(`/api/campaigns/${id}`, patch),
  cloneCampaign: (id: number, req: CampaignCloneRequest) => api.post<Campaign>(`/api/campaigns/${id}/clone`, req),
  campaignUsers: (id: number) => api.get<unknown>(`/api/campaigns/${id}/users`).then(list<CampaignUser>('campaign setters')),
  setCampaignUsers: (id: number, userIds: number[]) => api.put<Campaign>(`/api/campaigns/${id}/users`, { userIds }),
  importCsv: (id: number, csv: string, filename: string) => api.post<ImportReport>(`/api/campaigns/${id}/import`, { csv, filename }),
  imports: (id: number) => api.get<unknown>(`/api/campaigns/${id}/imports`).then(list<ImportSummary>('imports')),
  importReport: (id: number, importId: number) => api.get<ImportReport>(`/api/campaigns/${id}/imports/${importId}`),
  members: (id: number, q: { status?: string; q?: string; limit?: number; offset?: number }) =>
    api.get<Page<MemberRow> | MemberRow[]>(`/api/campaigns/${id}/memberships${qs(q)}`)
      .then((d) => (Array.isArray(d) ? { items: d, total: d.length } : d)),

  // lookups + briefs
  lookupsPending: (campaignId?: number) => api.get<LookupPending>(`/api/settings/lookups/pending${qs({ campaignId })}`),
  lookupsRun: (req: { campaignId?: number; limit: number }) => api.post<LookupRunResult>('/api/settings/lookups/run', req),
  aiStatus: () => api.get<AiStatus>('/api/ai/status'),
  briefsRun: (req: { campaignId: number; limit: number }) => api.post<BriefsRunResponse>('/api/ai/briefs/run', req),

  // users (CORE)
  users: () => api.get<unknown>('/api/users').then(list<UserAdmin>('users')),
  createUser: (req: UserCreateRequest) => api.post<UserAdmin>('/api/users', req),
  updateUser: (id: number, patch: UserPatchRequest) => api.patch<UserAdmin>(`/api/users/${id}`, patch),
  setUserCampaigns: (id: number, campaignIds: number[]) => api.put<UserAdmin>(`/api/users/${id}/campaigns`, { campaignIds }),

  // settings (CORE)
  settings: () => api.get<AdminSettings>('/api/settings'),
  patchSettings: (patch: AdminSettingsPatch) => api.patch<AdminSettings>('/api/settings', patch),
  // Booked meetings → Discord (migration 0027): a real test post; every failed post owed again.
  discordTest: () => api.post<DiscordTestResult & { settings: AdminSettings }>('/api/settings/discord/test'),
  discordRetryFailed: () => api.post<{ reset: number; settings: AdminSettings }>('/api/settings/discord/retry-failed'),
  stateRules: () => api.get<unknown>('/api/settings/state-rules').then(list<StateRule>('state rules')),
  patchStateRule: (code: string, patch: AdminStateRulePatch) => api.patch<StateRule>(`/api/settings/state-rules/${encodeURIComponent(code)}`, patch),
  dnc: (q: { q?: string; limit?: number; offset?: number }) =>
    api.get<Page<DncEntry> | DncEntry[]>(`/api/settings/dnc${qs(q)}`).then((d) => (Array.isArray(d) ? { items: d, total: d.length } : d)),
  addDnc: (phone: string, reason: string) => api.post<DncEntry>('/api/settings/dnc', { phone, reason }),
  audit: (q: { action?: string; userId?: number; entity?: string; entityId?: string; before?: number; limit?: number }) =>
    api.get<unknown>(`/api/settings/audit${qs(q)}`).then(list<AuditEntry>('the audit log')),

  // reports (CORE)
  funnel: (q: { from: string; to: string; groupBy: string; campaignId?: number; userId?: number }) =>
    api.get<unknown>(`/api/reports/funnel${qs(q)}`).then(list<FunnelRow>('the funnel report')),
  hours: (q: { from: string; to: string; campaignId?: number }) =>
    api.get<unknown>(`/api/reports/hours${qs(q)}`).then(list<HourBucket>('the hour report')),
  today: () => api.get<TodayReport>('/api/reports/today'),

  // numbers (TELEPHONY, admin only) — src/shared/api-telephony.ts
  numbers: () => api.get<unknown>('/api/numbers').then(list<OurNumber>('numbers')),
  searchNumbers: (q: { country: Country; areaCode: string; contains?: string }) =>
    api.get<unknown>(`/api/numbers/search${qs(q)}`).then(list<AvailableNumber>('the number search')),
  buyNumber: (req: NumberBuyRequest) => api.post<OurNumber>('/api/numbers/buy', req),
  importNumber: (req: NumberImportRequest) => api.post<OurNumber>('/api/numbers/import', req),
  patchNumber: (id: number, patch: NumberPatchRequest) => api.patch<OurNumber>(`/api/numbers/${id}`, patch),
  numberEvents: (id: number) => api.get<unknown>(`/api/numbers/${id}/events`).then(list<NumberEvent>('the number ledger')),
  addNumberEvent: (id: number, kind: ManualNumberEventKind, note: string) => api.post<NumberEvent>(`/api/numbers/${id}/events`, { kind, note }),
  numberDaily: (id: number, days = 30) => api.get<unknown>(`/api/numbers/${id}/daily${qs({ days })}`).then(list<NumberDailyRow>('daily number stats')),

  // voicemail-drop recordings (TELEPHONY)
  recordings: () => api.get<unknown>('/api/recordings').then(list<VmRecording>('recordings')),
  /** Whether this user's uploads are approved on the spot (admins and Growth Specialists, who tick the checklist). */
  recordingPolicy: () => api.get<{ selfApprove: boolean }>('/api/recordings/policy'),
  uploadRecording: (wav: Blob, fields: { situation: string; campaignId: number | null; scriptText: string; checklist?: Record<string, boolean> }) =>
    api.upload<VmRecording>('/api/recordings', wav, {
      situation: fields.situation,
      ...(fields.campaignId === null ? {} : { campaignId: String(fields.campaignId) }),
      scriptText: fields.scriptText,
      humanVoiceAttested: 'true',
      ...(fields.checklist ? { checklist: JSON.stringify(fields.checklist) } : {}),
    }),
  reviewRecording: (id: number, review: RecordingReview) => api.patch<VmRecording>(`/api/recordings/${id}`, review),
  /** deleted=false/rejected=true when a call already played it (kept for the record, marked rejected). */
  deleteRecording: (id: number) => api.del<{ ok: true; deleted: boolean; rejected: boolean }>(`/api/recordings/${id}`),
};
